import { api } from './api'
import { readScopedString, writeScopedString } from './scopedStorage'

// Findings #133/#134 (rehearsal 2): readings and records of a deleted lot stay
// (they happened in that pond — GLOBALG.A.P. / ASC record keeping), but showed
// as "sin lote" in Agua and as "Activo" in Biomasa. This loads the farm's
// deleted lots once per farm (online, best-effort) so those screens can say
// "PRUEBA-OVAS (lote eliminado)".
//
// Findings #200: the list is also kept on the device, so Base de datos still
// recognises a deleted lot's rows offline. `null` means "not known on this
// device" (offline before the first load): screens then behave as before.

export type DeletedLotRef = { id: string; lotKey: string; productionUnitId?: string; deletedAt?: string }

const cache = new Map<string, Promise<DeletedLotRef[] | null>>()

const storageKey = (farmId: string) => `deleted-lots:${farmId}`

const readStored = (farmId: string): DeletedLotRef[] | null => {
  try {
    const raw = readScopedString(storageKey(farmId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed as DeletedLotRef[] : null
  } catch {
    return null
  }
}

export const loadDeletedLots = (farmId: string): Promise<DeletedLotRef[] | null> => {
  const key = farmId.trim()
  if (!key) return Promise.resolve([])
  const cached = cache.get(key)
  if (cached) return cached
  const pending = api.lots.deleted(key)
    .then((lots) => {
      const refs: DeletedLotRef[] = (lots ?? []).map((lot) => ({
        id: String(lot.id),
        lotKey: String(lot.lotKey ?? ''),
        ...(lot.productionUnitId ? { productionUnitId: String(lot.productionUnitId) } : {}),
        ...(lot.deletedAt ? { deletedAt: String(lot.deletedAt) } : {})
      }))
      try {
        writeScopedString(storageKey(key), JSON.stringify(refs))
      } catch {
        // ignore
      }
      return refs
    })
    .catch(() => {
      cache.delete(key)
      return readStored(key)
    })
  cache.set(key, pending)
  return pending
}

export const loadDeletedLotLabels = async (farmId: string): Promise<Map<string, string>> => {
  const lots = await loadDeletedLots(farmId)
  return new Map((lots ?? []).map((lot) => [lot.id, lot.lotKey] as const))
}

/** Forget the cache (after a lot is deleted or restored in this session). */
export const resetDeletedLotLabels = (): void => {
  cache.clear()
}

type FarmLotRef = { id: string; lotKey?: string; productionUnitId?: string; createdAt?: string }

type RowLotFields = {
  lotId?: unknown
  lot?: unknown
  unmappedLot?: unknown
  productionUnitId?: unknown
  recordedAt?: unknown
  date?: unknown
}

const nameToken = (value: unknown) => String(value ?? '').trim().toLowerCase()

const timeMs = (value: unknown): number => {
  const text = String(value ?? '').trim()
  return text ? Date.parse(text) : Number.NaN
}

/**
 * Findings #200: tells which deleted lot a production row or log belongs to,
 * so current views (Base de datos, Biomasa, Panel) can leave it out. The row
 * itself is never changed or removed, and Restaurar brings it back.
 *
 * A record belongs to a deleted lot when:
 * - its lotId is a deleted lot; or
 * - its lotId is a lot record with no unit (the arrival record of "Sembrar en
 *   varias unidades") named like a deleted lot of the record's unit: rows Base
 *   de datos wrongly re-linked to that record before this fix; or
 * - it has no lotId and its label names a deleted lot of the record's unit
 *   while no active lot of that unit has that name (rows cleared to "Sin
 *   mapear"); or
 * - findings #207: it has no lotId, an active lot of the unit has the same
 *   name, and it was recorded before that active lot was created and not
 *   after the deleted lot was deleted.
 *
 * Returns the deleted lot's id, or null.
 */
export const buildDeletedLotResolver = (
  deletedLots: ReadonlyArray<DeletedLotRef>,
  farmLots: ReadonlyArray<FarmLotRef>
): ((row: RowLotFields, fallbackUnitId?: string) => string | null) => {
  if (deletedLots.length === 0) return () => null
  const deletedIds = new Set(deletedLots.map((lot) => String(lot.id)))
  const deletedByUnitName = new Map<string, DeletedLotRef[]>()
  deletedLots.forEach((lot) => {
    const unitId = String(lot.productionUnitId ?? '')
    if (!unitId) return
    const key = `${unitId}::${nameToken(lot.lotKey)}`
    deletedByUnitName.set(key, [...(deletedByUnitName.get(key) ?? []), lot])
  })
  const unitlessNameById = new Map<string, string>()
  const activeCreatedMsByUnitName = new Map<string, number[]>()
  farmLots.forEach((lot) => {
    const unitId = String(lot.productionUnitId ?? '')
    if (!unitId) {
      unitlessNameById.set(String(lot.id), nameToken(lot.lotKey))
      return
    }
    const key = `${unitId}::${nameToken(lot.lotKey)}`
    activeCreatedMsByUnitName.set(key, [...(activeCreatedMsByUnitName.get(key) ?? []), timeMs(lot.createdAt)])
  })
  // The latest deleted lot of that name that already existed at `atMs`
  // (unknown time: the latest one).
  const pickDeleted = (candidates: DeletedLotRef[], atMs: number): DeletedLotRef | undefined => {
    const live = Number.isFinite(atMs)
      ? candidates.filter((lot) => !Number.isFinite(timeMs(lot.deletedAt)) || atMs <= timeMs(lot.deletedAt))
      : candidates
    return [...(live.length > 0 ? live : candidates)]
      .sort((a, b) => (timeMs(a.deletedAt) || 0) - (timeMs(b.deletedAt) || 0))
      .at(-1)
  }

  return (row, fallbackUnitId) => {
    const unitId = String(row.productionUnitId ?? '').trim() || String(fallbackUnitId ?? '')
    const lotId = String(row.lotId ?? '').trim()
    const atMs = timeMs(row.recordedAt ?? row.date)
    if (lotId) {
      if (deletedIds.has(lotId)) return lotId
      const unitlessName = unitlessNameById.get(lotId)
      if (unitlessName === undefined) return null
      const candidates = deletedByUnitName.get(`${unitId}::${unitlessName}`)
      return candidates ? pickDeleted(candidates, atMs)?.id ?? null : null
    }
    const label = nameToken(row.lot) || nameToken(row.unmappedLot)
    if (!label) return null
    const candidates = deletedByUnitName.get(`${unitId}::${label}`)
    if (!candidates) return null
    const activeCreated = activeCreatedMsByUnitName.get(`${unitId}::${label}`)
    if (!activeCreated) return pickDeleted(candidates, atMs)?.id ?? null
    // Same name as an active lot: only a log (it has an exact recordedAt; a
    // row's day is too coarse) that predates every active lot of that name
    // and falls within a deleted lot's life is the deleted lot's. Without
    // times it stays with the active lot.
    const eventMs = timeMs(row.recordedAt)
    if (!Number.isFinite(eventMs) || !activeCreated.every((createdMs) => Number.isFinite(createdMs) && eventMs < createdMs)) return null
    const owner = candidates.find((lot) => Number.isFinite(timeMs(lot.deletedAt)) && eventMs <= timeMs(lot.deletedAt))
    return owner?.id ?? null
  }
}

/** `buildDeletedLotResolver` as a yes/no test. */
export const buildDeletedLotRowMatcher = (
  deletedLots: ReadonlyArray<DeletedLotRef>,
  farmLots: ReadonlyArray<FarmLotRef>
): ((row: RowLotFields, fallbackUnitId?: string) => boolean) => {
  const resolve = buildDeletedLotResolver(deletedLots, farmLots)
  return (row, fallbackUnitId) => resolve(row, fallbackUnitId) !== null
}

/**
 * Findings #207: the logs that do not belong to a deleted lot. Overlays match
 * logs to rows by day and lot name, so without this a deleted lot's deaths or
 * weights count for an active lot with the same name.
 */
export const withoutDeletedLotLogs = <T extends RowLotFields>(
  logs: T[],
  isDeleted: (row: RowLotFields, fallbackUnitId?: string) => boolean
): T[] => {
  const kept = logs.filter((log) => !isDeleted(log))
  return kept.length === logs.length ? logs : kept
}

type LotLife = 'current' | 'deleted'

/** Findings #207: logs of active lots (`current`) and of deleted lots (`deleted`). */
export const splitLogsByDeletedLot = <T extends RowLotFields>(
  logs: T[],
  isDeleted: (row: RowLotFields, fallbackUnitId?: string) => boolean
): Record<LotLife, T[]> => {
  const deleted = logs.filter((log) => isDeleted(log))
  return { current: deleted.length === 0 ? logs : logs.filter((log) => !isDeleted(log)), deleted }
}

const splitRowsByLotLife = <R extends { id?: unknown }>(rows: R[], deletedRowIds: ReadonlySet<string>): Record<LotLife, R[]> => ({
  current: rows.filter((row) => !deletedRowIds.has(String(row.id))),
  deleted: rows.filter((row) => deletedRowIds.has(String(row.id)))
})

/**
 * Findings #207: runs a by-name overlay separately over the active lots' rows
 * (with the active lots' logs) and the deleted lots' rows (with theirs),
 * keeping the row order.
 */
export const overlayByRowLotLife = <R extends { id?: unknown }>(
  rows: R[],
  deletedRowIds: ReadonlySet<string>,
  overlay: (rows: R[], life: LotLife) => R[]
): R[] => {
  if (deletedRowIds.size === 0) return overlay(rows, 'current')
  const split = splitRowsByLotLife(rows, deletedRowIds)
  const byId = new Map<string, R>()
  overlay(split.current, 'current').forEach((row) => byId.set(String(row.id), row))
  overlay(split.deleted, 'deleted').forEach((row) => byId.set(String(row.id), row))
  return rows.map((row) => byId.get(String(row.id)) ?? row)
}

/** `overlayByRowLotLife` for a result keyed by row id (rollups). */
export const mergeByRowLotLife = <R extends { id?: unknown }, V>(
  rows: R[],
  deletedRowIds: ReadonlySet<string>,
  compute: (rows: R[], life: LotLife) => Map<string, V>
): Map<string, V> => {
  if (deletedRowIds.size === 0) return compute(rows, 'current')
  const split = splitRowsByLotLife(rows, deletedRowIds)
  return new Map([...compute(split.current, 'current'), ...compute(split.deleted, 'deleted')])
}
